import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { 
  Activity, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Clock,
  Play,
  Pause,
  RotateCcw
} from 'lucide-react';

interface ValidationEvent {
  id: string;
  bill_id: string;
  rule_id: string;
  status: 'running' | 'passed' | 'failed' | 'warning';
  message: string;
  timestamp: string;
  processing_time_ms: number;
  confidence_score: number;
}

interface ValidationStats {
  total_validations: number;
  passed: number;
  failed: number;
  warnings: number;
  avg_processing_time: number;
  success_rate: number;
}

export const RealTimeValidationMonitor: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isMonitoring, setIsMonitoring] = useState(true);
  const [events, setEvents] = useState<ValidationEvent[]>([]);
  const [stats, setStats] = useState<ValidationStats>({
    total_validations: 0,
    passed: 0,
    failed: 0,
    warnings: 0,
    avg_processing_time: 0,
    success_rate: 0
  });

  useEffect(() => {
    if (!user?.id || !isMonitoring) return;

    // Set up real-time subscription for validation events
    const channel = supabase
      .channel('validation-monitor')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'processing_logs',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          const newEvent = payload.new as any;
          if (newEvent?.stage === 'validation') {
            const validationEvent: ValidationEvent = {
              id: newEvent.id,
              bill_id: newEvent.bill_id,
              rule_id: newEvent.details?.rule_id || 'unknown',
              status: mapStatusToValidationStatus(newEvent.status),
              message: newEvent.details?.message || `Validation ${newEvent.status}`,
              timestamp: newEvent.created_at,
              processing_time_ms: newEvent.processing_time_ms || 0,
              confidence_score: newEvent.details?.confidence_score || 0
            };

            setEvents(prev => [validationEvent, ...prev.slice(0, 99)]); // Keep last 100 events
            updateStats(validationEvent);

            // Show toast for critical events
            if (validationEvent.status === 'failed') {
              toast({
                title: "Validation Failed",
                description: validationEvent.message,
                variant: "destructive"
              });
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, isMonitoring, toast]);

  const mapStatusToValidationStatus = (status: string): ValidationEvent['status'] => {
    switch (status) {
      case 'running': return 'running';
      case 'completed': return 'passed';
      case 'error': return 'failed';
      case 'warning': return 'warning';
      default: return 'running';
    }
  };

  const updateStats = (newEvent: ValidationEvent) => {
    setStats(prev => {
      const total = prev.total_validations + 1;
      const passed = prev.passed + (newEvent.status === 'passed' ? 1 : 0);
      const failed = prev.failed + (newEvent.status === 'failed' ? 1 : 0);
      const warnings = prev.warnings + (newEvent.status === 'warning' ? 1 : 0);
      const avgTime = (prev.avg_processing_time * prev.total_validations + newEvent.processing_time_ms) / total;
      const successRate = (passed / total) * 100;

      return {
        total_validations: total,
        passed,
        failed,
        warnings,
        avg_processing_time: avgTime,
        success_rate: successRate
      };
    });
  };

  const toggleMonitoring = () => {
    setIsMonitoring(!isMonitoring);
    toast({
      title: isMonitoring ? "Monitoring Paused" : "Monitoring Started",
      description: isMonitoring ? "Real-time validation monitoring has been paused" : "Real-time validation monitoring is now active"
    });
  };

  const clearEvents = () => {
    setEvents([]);
    setStats({
      total_validations: 0,
      passed: 0,
      failed: 0,
      warnings: 0,
      avg_processing_time: 0,
      success_rate: 0
    });
    toast({
      title: "Events Cleared",
      description: "All validation events have been cleared"
    });
  };

  const getStatusIcon = (status: ValidationEvent['status']) => {
    switch (status) {
      case 'running': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'passed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'warning': return <AlertTriangle className="h-4 w-4 text-orange-500" />;
    }
  };

  const getStatusColor = (status: ValidationEvent['status']) => {
    switch (status) {
      case 'running': return 'bg-yellow-100 text-yellow-800';
      case 'passed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'warning': return 'bg-orange-100 text-orange-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Activity className="h-6 w-6" />
          <h2 className="text-2xl font-semibold">Real-Time Validation Monitor</h2>
          <Badge variant={isMonitoring ? "default" : "secondary"}>
            {isMonitoring ? "Active" : "Paused"}
          </Badge>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleMonitoring}
          >
            {isMonitoring ? <Pause className="h-4 w-4 mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            {isMonitoring ? "Pause" : "Start"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={clearEvents}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Clear
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Validations</p>
                <p className="text-2xl font-bold">{stats.total_validations}</p>
              </div>
              <Activity className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Success Rate</p>
                <p className="text-2xl font-bold">{stats.success_rate.toFixed(1)}%</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <Progress value={stats.success_rate} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Processing Time</p>
                <p className="text-2xl font-bold">{stats.avg_processing_time.toFixed(0)}ms</p>
              </div>
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Failed Validations</p>
                <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Events Feed */}
      <Card>
        <CardHeader>
          <CardTitle>Validation Events</CardTitle>
          <CardDescription>
            Real-time feed of validation events across all bills
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-96">
            {events.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {isMonitoring ? "No validation events yet..." : "Monitoring is paused"}
              </div>
            ) : (
              <div className="space-y-2">
                {events.map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(event.status)}
                      <div>
                        <p className="font-medium">{event.message}</p>
                        <p className="text-sm text-muted-foreground">
                          Bill: {event.bill_id.slice(0, 8)}... • Rule: {event.rule_id.slice(0, 8)}...
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 text-right">
                      <Badge className={getStatusColor(event.status)}>
                        {event.status}
                      </Badge>
                      <div className="text-sm text-muted-foreground">
                        <p>{event.processing_time_ms}ms</p>
                        <p>{new Date(event.timestamp).toLocaleTimeString()}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};